"use client";

import {
    figmamicon,
    framericon,
    GdrAboutBgHomeImage,
    mysqlicon,
    nextjicon,
    nodejsicon,
    postmanicon,
    reactjsicon,
    typescripticon,
} from "@/lib/images";
import { LiquidGlass } from "@liquidglass/react";
import Image from "next/image";

function GdrAboutLiquidTagPill({ label, width }: { label: string; width: number }) {
    return (
        <span className="gdr-pill-wrap shrink-0" style={{ width: `${width}px` }}>
            <LiquidGlass
                borderRadius={999}
                blur={0.1}
                contrast={1.5}
                brightness={1.2}
                saturation={1.3}
                shadowIntensity={0.4}
                elasticity={0.8}
                displacementScale={0.5}
                zIndex={1}
                className="gdr-liquid-pill"
            >
                <span className="gdr-pill-label">{label}</span>
            </LiquidGlass>
        </span>
    );
}

export default function AboutSectionGdrDevelopment() {
    return (
        <section className="relative w-full overflow-hidden bg-black">
            <div className="max-w-[1320px] mx-auto">

                {/* BACKGROUND */}
                <div className="pointer-events-none absolute inset-0">
                    <div className="relative h-full w-full max-w-[1320px] mx-auto flex">
                        <div
                            className="relative z-10"
                            style={{
                                background: "#73C03333",
                                boxShadow: "0px 4px 250px 194px #73C03333",
                            }}
                        />
                        <div className="relative h-full w-[37.5%] z-0">
                            <Image
                                src={GdrAboutBgHomeImage}
                                alt="GDR Development building wireframe"
                                fill
                                priority
                                className="object-cover opacity-40"
                            />
                        </div>
                    </div>

                    <div className="absolute inset-0 bg-linear-to-r from-black/80 via-black/70 to-black/85" />
                </div>

                {/* CONTENT */}
                <div className="relative xl:px-20 px-6 xl:py-18 py-10 flex xl:flex-row flex-col justify-between items-start">

                    <p className="text-xs sm:text-sm tracking-[0.25em] uppercase text-[#FFFFFFA6] mb-3">
                        About
                    </p>

                    <div className="xl:w-[874px] w-full">

                        <h2 className="lg:text-[40px] md:text-[32px] sm:text-[28px] text-[24px] text-white leading-tight">
                            Design a high-conversion website that positions GDR Development as
                            <span className="text-[#FFFFFF80]">
                                {" "}a results-driven Real Estate and encourages users to
                            </span>
                        </h2>

                        <p className="mt-2 text-[20px] text-[#FFFFFFCC]">
                            The goal was to design a modern and visually appealing website
                            which features all the ongoing as well as upcoming projects.
                        </p>

                        {/* Tools */}
                        <div className="mt-10">
                            <div className="rounded-[28px] border border-white/15 px-6 py-6 sm:px-10 sm:py-7">

                                <p className="text-[#FFFFFFCC] mb-5 uppercase">
                                    Tools & Tech Used
                                </p>

                                <div className="grid lg:grid-cols-4 sm:grid-cols-2 gap-5 justify-items-center">
                                    {[
                                        { label: "Figma", icon: figmamicon, cardWidth: 190, cardHeight: 64, iconWidth: 55, iconHeight: 55 },
                                        { label: "NextJS", icon: nextjicon, cardWidth: 190, cardHeight: 64, iconWidth: 45, iconHeight: 45 },
                                        { label: "Typescript", icon: typescripticon, cardWidth: 190, cardHeight: 64, iconWidth: 32, iconHeight: 32 },
                                        { label: "Framer Motion", icon: framericon, cardWidth: 190, cardHeight: 64, iconWidth: 32, iconHeight: 32 },
                                        { label: "NodeJS", icon: nodejsicon, cardWidth: 190, cardHeight: 64, iconWidth: 32, iconHeight: 32 },
                                        { label: "MySQL", icon: mysqlicon, cardWidth: 190, cardHeight: 64, iconWidth: 32, iconHeight: 32 },
                                        { label: "Postman", icon: postmanicon, cardWidth: 190, cardHeight: 64, iconWidth: 32, iconHeight: 32 },
                                        { label: "ReactJS", icon: reactjsicon, cardWidth: 190, cardHeight: 64, iconWidth: 32, iconHeight: 32 },
                                    ].map((item) => (
                                        <div
                                            key={item.label}
                                            style={{
                                                width: `${item.cardWidth}px`,
                                                minWidth: `${item.cardWidth}px`,
                                                maxWidth: `${item.cardWidth}px`,
                                                height: `${item.cardHeight}px`,
                                                minHeight: `${item.cardHeight}px`,
                                                maxHeight: `${item.cardHeight}px`,
                                            }}
                                            className="flex items-center justify-center gap-3 rounded-full px-5 border border-[#D9EDFE24]"
                                        >
                                            <span
                                                className="relative overflow-hidden shrink-0"
                                                style={{
                                                    width: `${item.iconWidth}px`,
                                                    height: `${item.iconHeight}px`,
                                                }}
                                            >
                                                <Image
                                                    src={item.icon}
                                                    alt={item.label}
                                                    fill
                                                    className="object-contain"
                                                />
                                            </span>
                                            <span className="text-[#FFFFFFCC] whitespace-nowrap">
                                                {item.label}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* TAGS */}
                        <div className="mt-15 flex justify-center lg:justify-end">
                            <div className="relative z-10 flex flex-wrap lg:flex-nowrap items-center justify-center lg:justify-end gap-5">
                                {[
                                    { label: "Case Study", width: 158 },
                                    { label: "Branding", width: 158 },
                                    { label: "Development", width: 190 },
                                    { label: "Web Designing", width: 190 },
                                ].map((tag) => (
                                    <GdrAboutLiquidTagPill key={tag.label} label={tag.label} width={tag.width} />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}